import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Copy, CheckCircle2, Bug, Receipt, MessageSquare, Send } from "lucide-react";

export default function Feedback() {
  const [copied, setCopied] = useState(false);
  const [type, setType] = useState("bug");
  const [priority, setPriority] = useState("normal");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [details, setDetails] = useState("");
  const [steps, setSteps] = useState("");
  const [expected, setExpected] = useState("");
  const [actual, setActual] = useState("");

  const envInfo = useMemo(() => {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      appVersion: (import.meta as any).env?.VITE_APP_VERSION || "dev",
      pathname: window.location.pathname,
    };
  }, []);

  const payload = useMemo(() => ({
    type,
    priority,
    email,
    subject,
    details,
    steps,
    expected,
    actual,
    env: envInfo,
  }), [type, priority, email, subject, details, steps, expected, actual, envInfo]);

  const copyPayload = async () => {
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const sendViaEmail = () => {
    const mailTo = "info@manageaze.com";
    const subjBase = subject?.trim() ? subject.trim() : `Manageaze ${type} feedback`;
    const subjectLine = `${subjBase} (${priority})`;

    const lines: string[] = [];
    lines.push(`# Feedback`);
    lines.push(`Type: ${type}`);
    lines.push(`Priority: ${priority}`);
    if (email) lines.push(`From: ${email}`);
    lines.push("");
    if (details) {
      lines.push(`Description:`);
      lines.push(details);
      lines.push("");
    }
    if (type === "bug") {
      if (steps) { lines.push(`Steps to Reproduce:`); lines.push(steps); lines.push(""); }
      if (expected) { lines.push(`Expected:`); lines.push(expected); lines.push(""); }
      if (actual) { lines.push(`Actual:`); lines.push(actual); lines.push(""); }
    }
    lines.push(`Environment:`);
    lines.push(JSON.stringify(envInfo, null, 2));
    lines.push("");
    lines.push(`Raw Payload:`);
    lines.push(JSON.stringify(payload, null, 2));

    const body = encodeURIComponent(lines.join("\n"));
    const subjectEncoded = encodeURIComponent(subjectLine);
    window.location.href = `mailto:${mailTo}?subject=${subjectEncoded}&body=${body}`;
  };

  const iconForType = {
    bug: <Bug className="h-4 w-4" />,
    refund: <Receipt className="h-4 w-4" />,
    general: <MessageSquare className="h-4 w-4" />,
  } as const;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-medium text-foreground">Feedback</h1>
        <p className="text-muted-foreground">Report bugs, request refunds, or share general feedback. We’ll use the info below to help resolve your issue fast.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Tell us what happened</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Feedback type</label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="refund">Billing Issues</SelectItem>
                    <SelectItem value="general">General issue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Priority</label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Your email (for follow-up)</label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Subject</label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Describe the issue or request in detail" className="min-h-28" />
            </div>

            {type === "bug" && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2 sm:col-span-1">
                  <label className="text-sm font-medium">Steps to reproduce</label>
                  <Textarea value={steps} onChange={(e) => setSteps(e.target.value)} placeholder="1) Go to ...\n2) Click ...\n3) See error" className="min-h-28" />
                </div>
                <div className="space-y-2 sm:col-span-1">
                  <label className="text-sm font-medium">Expected behavior</label>
                  <Textarea value={expected} onChange={(e) => setExpected(e.target.value)} placeholder="What you expected to happen" className="min-h-28" />
                </div>
                <div className="space-y-2 sm:col-span-1">
                  <label className="text-sm font-medium">Actual behavior</label>
                  <Textarea value={actual} onChange={(e) => setActual(e.target.value)} placeholder="What actually happened" className="min-h-28" />
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button onClick={copyPayload}>
                <Copy className="h-4 w-4 mr-2" /> Copy report to clipboard
              </Button>
              <Button variant="secondary" onClick={sendViaEmail}>
                <Send className="h-4 w-4 mr-2" /> Send feedback
              </Button>
              {copied && (
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4 text-green-600" /> Copied!
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Helpful context</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{iconForType[type as keyof typeof iconForType]}</Badge>
                <span className="text-sm">Type: <strong className="font-medium capitalize">{type}</strong></span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-muted-foreground">Browser</div>
                <div className="break-words">{envInfo.userAgent}</div>
                <div className="text-muted-foreground">Language</div>
                <div>{envInfo.language}</div>
                <div className="text-muted-foreground">Platform</div>
                <div>{envInfo.platform}</div>
                <div className="text-muted-foreground">Viewport</div>
                <div>{envInfo.viewport.width} × {envInfo.viewport.height}</div>
                <div className="text-muted-foreground">Timezone</div>
                <div>{envInfo.timezone}</div>
                <div className="text-muted-foreground">Path</div>
                <div className="break-words">{envInfo.pathname}</div>
                <div className="text-muted-foreground">App Version</div>
                <div>{envInfo.appVersion}</div>
              </div>

              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-2">Preview of the payload we’ll receive:</p>
                <ScrollArea className="h-64 border rounded-md p-3 bg-muted/30">
                  <pre className="text-xs whitespace-pre-wrap break-words">{JSON.stringify(payload, null, 2)}</pre>
                </ScrollArea>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


